import React, { useCallback } from 'react';
import {
  FieldError as IFieldError,
  get,
  useFormContext
} from 'react-hook-form';
import {
  Flex,
  Popover,
  Box,
  PopoverBody,
  PopoverContent,
  PopoverTrigger
} from '@chakra-ui/react';
import { FormFeedback, Input } from '@morressier/alembic';
import { HexColorPicker } from 'react-colorful';

interface CustomColorPickerProps {
  formField: string;
}

const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;
const DEFAULT_PICKER_COLOR = '#000000';
const HEX_COLOR_EXAMPLE = '#ff0000';
const WHITESPACE_PATTERN = /\s/;

const isValidHexColor = (value: string) => HEX_COLOR_PATTERN.test(value);
const normalizePickerColor = (value: string) =>
  isValidHexColor(value) ? value : DEFAULT_PICKER_COLOR;
const validateColorValue = (value: string): true | string => {
  if (!value?.length) {
    return 'Tag category color is required';
  }

  if (WHITESPACE_PATTERN.test(value)) {
    return 'Color code cannot contain whitespace';
  }

  if (!isValidHexColor(value)) {
    return `Please enter a valid hex color code (e.g. ${HEX_COLOR_EXAMPLE})`;
  }

  return true;
};

const CustomColorPicker: React.FC<CustomColorPickerProps> = ({ formField }) => {
  const {
    watch,
    setValue,
    setError,
    clearErrors,
    register,
    formState: { errors }
  } = useFormContext()!;

  const colorValue = (watch(formField) as string) || '';
  const pickerColor = normalizePickerColor(colorValue);

  const updateColor = useCallback(
    (newColor: string) => {
      setValue(formField, newColor, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true
      });

      const validationResult = validateColorValue(newColor);

      if (typeof validationResult === 'string') {
        setError(formField, {
          type: 'validate',
          message: validationResult
        });
      } else {
        clearErrors(formField);
      }
    },
    [clearErrors, formField, setError, setValue]
  );

  const handleTextChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateColor(event.target.value);
    },
    [updateColor]
  );

  const handlePickerChange = useCallback(
    (newColor: string) => {
      updateColor(newColor);
    },
    [updateColor]
  );

  const error = get(errors, formField) as IFieldError;
  const hasError = !!error;

  return (
    <Flex direction={'column'} gap={1}>
      <Flex gap={3} width={'sm'}>
        <Input
          value={colorValue}
          {...register(formField, {
            required: 'Tag category color is required',
            validate: value => validateColorValue(value)
          })}
          onChange={handleTextChange}
          placeholder="#ff0000"
          maxLength={7}
          isInvalid={hasError}
        />
        <Popover placement="bottom-start" trigger="click">
          <PopoverTrigger>
            <Box
              as="button"
              type="button"
              w="40px"
              h="35px"
              bg={isValidHexColor(colorValue) ? colorValue : 'transparent'}
              border="1px solid"
              borderColor={hasError ? 'red.400' : 'gray.300'}
              borderRadius="4px"
              shadow={'sm'}
              cursor={'pointer'}
            />
          </PopoverTrigger>
          <PopoverContent
            w="280px"
            p={3}
            border="1px solid"
            borderColor="blue.300"
            borderRadius="8px"
            boxShadow="md"
          >
            <PopoverBody p={0}>
              <HexColorPicker
                color={pickerColor}
                onChange={handlePickerChange}
                style={{ width: '100%', borderRadius: '6px' }}
              />
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Flex>
      {hasError && (
        <FormFeedback variant="error">{error?.message}</FormFeedback>
      )}
    </Flex>
  );
};

export default CustomColorPicker;
